import { api } from './http';
import axios from 'axios';
import type {
  StudyFile,
  StudyFileUpload,
  StudyFilesFilters,
  StudyFilesResponse,
} from '@/types/studyFiles';
import { API_ENDPOINTS } from '@/config/constants';

// Study Files API functions
export async function getStudyFiles(
  filters?: StudyFilesFilters
): Promise<StudyFilesResponse> {
  try {
    const params = new URLSearchParams();

    // study_id no longer sent as it's now in JWT token
    if (filters?.page) {
      params.append('page', filters.page.toString());
    }

    if (filters?.per_page) {
      params.append('per_page', filters.per_page.toString());
    }

    if (filters?.order) {
      params.append('order', filters.order);
    }

    if (filters?.direction) {
      params.append('direction', filters.direction);
    }

    // TODO: Implement whitelist filtering when backend supports it
    if (filters?.exclude_whitelisted) {
      params.append('exclude_whitelisted', 'true');
    }

    const url = `${API_ENDPOINTS.STUDY_FILES}${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);

    // Debug logging

    // Extract data from the response structure
    if (
      response.data &&
      response.data.data &&
      Array.isArray(response.data.data) &&
      response.data.pagination
    ) {
      return {
        data: response.data.data,
        pagination: response.data.pagination,
      };
    }

    // Fallback for direct array response (should not happen with proper API)
    if (Array.isArray(response.data)) {
      return {
        data: response.data,
        pagination: {
          current_page: 1,
          next_page: null,
          prev_page: null,
          total_pages: 1,
          total_count: response.data.length,
          per_page: response.data.length,
        },
      };
    }

    return {
      data: [],
      pagination: {
        current_page: 1,
        next_page: null,
        prev_page: null,
        total_pages: 0,
        total_count: 0,
        per_page: 25,
      },
    };
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'error' in error.response.data &&
      typeof error.response.data.error === 'string'
    ) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Failed to fetch study files');
  }
}

export async function uploadStudyFile(
  uploadData: StudyFileUpload,
  onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void,
  options?: { signal?: AbortSignal }
): Promise<StudyFile> {
  try {
    const formData = new FormData();
    formData.append('file', uploadData.file);
    // study_id no longer sent as it's now in JWT token

    const response = await api.post(API_ENDPOINTS.STUDY_FILES, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onUploadProgress
        ? (progressEvent) => {
            if (progressEvent.total) {
              onUploadProgress({
                loaded: progressEvent.loaded,
                total: progressEvent.total,
              });
            }
          }
        : undefined,
      signal: options?.signal,
    });

    return response.data;
  } catch (error: unknown) {
    // Preserve cancellation semantics for callers (AbortController)
    if (
      axios.isCancel(error) ||
      (error &&
        typeof error === 'object' &&
        'code' in error &&
        (error as { code?: unknown }).code === 'ERR_CANCELED') ||
      (error instanceof Error &&
        (error.name === 'CanceledError' || error.name === 'AbortError'))
    ) {
      throw error;
    }

    // Handle HTTP status errors (like 413 Request Entity Too Large)
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'status' in error.response
    ) {
      const status = error.response.status as number;
      
      if (status === 413) {
        throw new Error('File is too large. Please upload a smaller file.');
      }
    }

    // Handle error response data
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'error' in error.response.data
    ) {
      const errorData = error.response.data.error;

      // Handle nested error object structure
      if (typeof errorData === 'object' && errorData !== null) {
        const { title, details } = errorData as {
          title?: string;
          details?: string;
        };
        if (title && details) {
          throw new Error(`${title}: ${details}`);
        } else if (title) {
          throw new Error(title);
        } else if (details) {
          throw new Error(details);
        } else {
          throw new Error('Upload failed');
        }
      } else if (typeof errorData === 'string') {
        throw new Error(errorData);
      }
    }
    throw new Error('Failed to upload study file');
  }
}

export async function deleteStudyFile(fileId: number): Promise<void> {
  try {
    // study_id no longer sent as it's now in JWT token
    await api.delete(`${API_ENDPOINTS.STUDY_FILES}/${fileId}`);
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'error' in error.response.data &&
      typeof error.response.data.error === 'string'
    ) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Failed to delete study file');
  }
}

export async function getStudyFileVersions(
  studyFileId: number
): Promise<import('@/types/studyFiles').StudyFileVersionsResponse> {
  try {
    const response = await api.get(
      `${API_ENDPOINTS.STUDY_FILES}/${studyFileId}/versions`
    );
    return response.data;
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'error' in error.response.data &&
      typeof error.response.data.error === 'string'
    ) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Failed to fetch study file versions');
  }
}

// Types for validate meta functionality
export interface FileMetadata {
  filename: string;
  size: number;
  content_type: string;
}

export interface ServerValidationResult {
  filename: string;
  error?: string;
}

export interface ValidateMetaResponse {
  data: ServerValidationResult[];
}

export async function validateStudyFilesMeta(
  metadata: FileMetadata[]
): Promise<ServerValidationResult[]> {
  try {
    const response = await api.post(API_ENDPOINTS.STUDY_FILES_VALIDATE_META, {
      metadata,
    });

    // Extract data from the response structure
    if (
      response.data &&
      response.data.data &&
      Array.isArray(response.data.data)
    ) {
      return response.data.data;
    }

    // Fallback for direct array response
    if (Array.isArray(response.data)) {
      return response.data;
    }

    return [];
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'error' in error.response.data &&
      typeof error.response.data.error === 'string'
    ) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Failed to validate study files metadata');
  }
}
